import type { TransactionDto } from "@home/shared";
import { useMemo, useState } from "react";
import { api } from "../api.ts";
import { EmptyState, SkeletonCard } from "../components/EmptyState.tsx";
import { Segmented } from "../components/Segmented.tsx";
import { TxDetailSheet } from "../components/TxDetailSheet.tsx";
import { TxList } from "../components/TxList.tsx";
import { currentMonth, dayTitle, localDay, money, monthTitle, shiftMonth } from "../format.ts";
import { t, type Lang } from "../i18n.ts";
import { useRemote } from "../useRemote.ts";

type Filter = "all" | "expense" | "income" | "transfer";

/** Все операции месяца по дням, с фильтром; нажатие — подробности и удаление. */
export function OperationsPage({ lang, onAdd, initialMonth }: { lang: Lang; onAdd: () => void; initialMonth?: string }) {
  const [month, setMonth] = useState(initialMonth ?? currentMonth());
  const [filter, setFilter] = useState<Filter>("all");
  const [picked, setPicked] = useState<TransactionDto | null>(null);
  // Тот же ключ, что у «Все операции» на обзоре — общий кэш.
  const remote = useRemote(`tx:${month}:all`, () => api.transactions(month).then((r) => r.transactions));
  const isCurrent = month === currentMonth();

  const { groups, income, expense } = useMemo(() => {
    const items = (remote.data ?? []).filter((tx) => filter === "all" || tx.type === filter);
    const byDay = new Map<string, TransactionDto[]>();
    for (const tx of items) {
      const day = localDay(tx.date);
      byDay.set(day, [...(byDay.get(day) ?? []), tx]);
    }
    return {
      groups: [...byDay.entries()].sort(([a], [b]) => b.localeCompare(a)),
      income: items.filter((tx) => tx.type === "income").reduce((s, tx) => s + tx.amountUzs, 0),
      expense: items.filter((tx) => tx.type === "expense").reduce((s, tx) => s + tx.amountUzs, 0),
    };
  }, [remote.data, filter]);

  return (
    <main className="dash">
      <header className="month-switch">
        <button className="icon-btn" onClick={() => setMonth(shiftMonth(month, -1))} aria-label="prev">
          ‹
        </button>
        <h2>{monthTitle(month, lang)}</h2>
        <button className="icon-btn" disabled={isCurrent} onClick={() => setMonth(shiftMonth(month, 1))} aria-label="next">
          ›
        </button>
      </header>

      <Segmented
        value={filter}
        onChange={setFilter}
        options={[
          { value: "all", label: t("all") },
          { value: "expense", label: t("expenses") },
          { value: "income", label: t("incomes") },
          { value: "transfer", label: "🔁" },
        ]}
      />

      {remote.data && (income > 0 || expense > 0) && (
        <div className="ops-summary">
          {income > 0 && (
            <span>
              {t("incomeL")} <b className="income">{money(income, "UZS", lang, true)}</b>
            </span>
          )}
          {expense > 0 && (
            <span>
              {t("expenseL")} <b>{money(-expense, "UZS", lang)}</b>
            </span>
          )}
        </div>
      )}

      {!remote.data && remote.loading && (
        <>
          <SkeletonCard lines={4} />
          <SkeletonCard lines={3} />
        </>
      )}
      {!remote.data && !remote.loading && remote.error != null && <p className="empty">{t("loadError")}</p>}

      {remote.data && remote.data.length > 0 && groups.length === 0 && <p className="empty">{t("noOpsFilter")}</p>}
      {remote.data && remote.data.length === 0 && (
        <EmptyState icon="🧾" title={t("noOps")} text={t("noOpsHint")} action={{ label: `＋ ${t("firstOp")}`, onClick: onAdd }} />
      )}

      {groups.map(([day, items]) => {
        const dayExpense = items.filter((tx) => tx.type === "expense").reduce((s, tx) => s + tx.amountUzs, 0);
        return (
          <section key={day} className="dash-card day-card">
            <div className="day-head">
              <span>{dayTitle(day, lang)}</span>
              {dayExpense > 0 && <span className="muted">{money(-dayExpense, "UZS", lang)}</span>}
            </div>
            <TxList items={items} lang={lang} onPick={setPicked} showDate={false} />
          </section>
        );
      })}

      <button className="fab" onClick={onAdd}>
        ＋ {t("addOp")}
      </button>
      {picked && <TxDetailSheet tx={picked} lang={lang} onClose={() => setPicked(null)} />}
    </main>
  );
}
